import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'

import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Hidden from '@mui/material/Hidden'
import Skeleton from '@mui/material/Skeleton'

import Header from 'components/nav/Header'
import TrendFilters from 'components/nav/secondary/TrendFilters'
import Footer from 'components/nav/Footer'

import DaysOnMarket from 'components/charts/DaysOnMarket'
import SoldPrice from 'components/charts/SoldPrice'
import TopPropertyTables from 'components/tables/TopPropertyTables'
import MarketReport from 'components/reports/MarketReport'
import InterestDialog from 'components/common/InterestDialog'
import MortgageForm from 'components/common/MortgageForm'

import realtor1 from 'assets/realtors/small_austin.png'
import realtor2 from 'assets/realtors/small_brett.png'
import realtor3 from 'assets/realtors/small_cyrus.png'
import realtor4 from 'assets/realtors/small_foster.png'
import realtor5 from 'assets/realtors/small_jason.png'
import realtor6 from 'assets/realtors/small_josh.png'
import realtor7 from 'assets/realtors/small_maddy.png'

const realtors = [
  realtor1,
  realtor2,
  realtor3,
  realtor4,
  realtor5,
  realtor6,
  realtor7,
]

const MarketStatistics = () => {
  const [city, setCity] = useState('Toronto')
  const [bedrooms, setBedrooms] = useState(4)
  const [data, setData] = useState(null)
  const [olderData, setOlderData] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [scrollPosition, setScrollPosition] = useState(0)
  const sidebarRef = useRef(null)

  const generateRandomHeadshot = () => {
    const randomIndex = Math.floor(Math.random() * realtors.length)
    setCurrentHeadshot(realtors[randomIndex])
  }
  const [currentHeadshot, setCurrentHeadshot] = useState(null)

  useEffect(() => {
    if (!currentHeadshot) generateRandomHeadshot()
  }, [currentHeadshot])

  const getMinSoldDate = (yearsAgo) => {
    const today = new Date()
    const pastDate = new Date(
      today.getFullYear() - yearsAgo,
      today.getMonth(),
      1
    ) // First day of that month
    return pastDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
  }

  const fetchMarketStats = async () => {
    const today = new Date()
    setLoading(true)
    setError(null)
    try {
      const response = await axios.get(
        `https://api.dev.thinkhomewise.com/realty/listings`,
        {
          params: {
            city,
            minBeds: 0,
            maxBeds: bedrooms,
            status: 'U',
            lastStatus: 'Sld',
            minSoldDate:
              olderData === 0
                ? new Date(today.getFullYear(), today.getMonth() - 6, 1)
                    .toISOString()
                    .split('T')[0]
                : getMinSoldDate(olderData),
            statistics:
              'avg-soldPrice,avg-listPrice,cnt-closed,pct-aboveBelowList,avg-daysOnMarket,grp-mth',
            listings: false,
            aggregateStatistics: true,
            aggregates: 'details.propertyType',
          },
        }
      )
      setData(response.data.statistics)
      console.log(response.data.statistics)
    } catch (err) {
      setError('Failed to fetch market data.')
    }
    setLoading(false)
  }
  useEffect(() => {
    fetchMarketStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUpdate = (filters) => {
    console.log('Fetching data with filters:', filters)
    // Trigger your data fetch logic here
    fetchMarketStats()
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (sidebarRef.current) {
      const sidebarHeight = sidebarRef.current.offsetHeight
      sidebarRef.current.style.height = `${sidebarHeight}px`
    }
  }, [scrollPosition])

  const sidebarClasses = ['sidebar sticky-sidebar']

  // if (scrollPosition >= 1) {
  //   sidebarClasses.push('sticky-sidebar')
  // }

  const handleScroll = () => {
    setScrollPosition(window.scrollY)
  }

  return (
    <>
      <Header />
      <TrendFilters
        city={city}
        setCity={setCity}
        bedrooms={bedrooms}
        setBedrooms={setBedrooms}
        olderData={olderData}
        setOlderData={setOlderData}
        onUpdate={handleUpdate}
      />
      <Container
        component="main"
        maxWidth="lg"
        style={{ paddingTop: `1rem`, paddingBottom: `2rem` }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {loading && (
              <Grid container spacing={8}>
                <Grid item xs={12}>
                  <h1 style={{ marginTop: `16px` }}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={280}
                      height={40}
                    />
                  </h1>
                </Grid>
              </Grid>
            )}
            {error && (
              <Container
                component="main"
                maxWidth="lg"
                style={{ padding: `5rem 0` }}
              >
                <Grid container sx={{ justifyContent: `center` }}>
                  <Grid item md={4}>
                    <Alert severity="error">
                      There is an error — check it out!
                    </Alert>
                  </Grid>
                </Grid>
              </Container>
            )}

            {!loading && !error && data && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {data && <MarketReport data={data} city={city} />}
                </Grid>

                <Grid item xs={12}>
                  <h2>Average Days on Market & Property Listings</h2>
                  <p>
                    The average number of days properties stay on the market
                    alongside the total number of properties listed for sale
                    each month in {city}.
                  </p>
                  {data.daysOnMarket && (
                    <DaysOnMarket data={data.daysOnMarket} />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <h2>Average Price & Sales Volume Over Time</h2>
                  <p>
                    This chart tracks the monthly average property price
                    alongside the number of properties sold&nbsp;in&nbsp;{city}.
                  </p>
                  {data.soldPrice && <SoldPrice data={data.soldPrice} />}
                </Grid>

                <Grid item xs={12}>
                  <h2>{city}'s Top 5 Property Types</h2>
                  <p>
                    Discover {city}'s most valuable property landscape at a
                    glance
                  </p>
                  {data.listPrice && (
                    <TopPropertyTables data={data.listPrice.aggregates} />
                  )}
                  <p>
                    <small>
                      Properties highlighted in blue represent exceptional
                      investment opportunities in {city}. Appearing in both
                      rankings, offering the perfect blend of market presence
                      and premium value.
                    </small>
                  </p>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Hidden mdDown>
            <Grid item xs={12} md={4}>
              <Box
                className={sidebarClasses.join(' ')}
                ref={sidebarRef}
                sx={{
                  margin: {
                    xs: `0 0`,
                    sm: `0 0`,
                    md: `1rem 0 0`,
                  },
                }}
              >
                <Paper
                  style={{
                    padding: `1rem 1.5rem 2rem`,
                    textAlign: `center`,
                    border: `1px solid rgba(0, 0, 0, 0.2)`,
                    boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)`,
                    borderRadius: `6px`,
                  }}
                >
                  <Box sx={{ display: `flex`, paddingBottom: `1rem` }}>
                    {currentHeadshot && (
                      <img
                        src={currentHeadshot}
                        width="65"
                        height="65"
                        alt="Homewise Realtor"
                      />
                    )}
                    <span style={{ width: `100%` }}>
                      <h3
                        style={{
                          marginTop: `.5rem`,
                          marginBottom: `0`,
                        }}
                      >
                        Contact Homewise Realtor
                      </h3>
                    </span>
                  </Box>
                  <InterestDialog
                    cta={'Contact Agent'}
                    color={'primary'}
                    variant={'contained'}
                    className={'primary'}
                    sx={{ width: `100%` }}
                    size={'small'}
                    headshot={currentHeadshot}
                    position={'detailPage_sideBar'}
                  />
                </Paper>

                <MortgageForm />
              </Box>
            </Grid>
          </Hidden>
        </Grid>
      </Container>
      <div
        style={{
          background: `#fff`,
          paddingTop: `2rem`,
          paddingBottom: `2rem`,
        }}
      >
        <Container component="main" maxWidth="lg">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={11}>
              <p>
                The data presented here is sourced from Repliers and reflects
                real estate market trends in {city} to the best of our ability.
                While we make every effort to ensure accuracy, we cannot
                guarantee that every number, decimal, or trend line is immune to
                the ever-changing nature of {city}’s housing market. Prices
                fluctuate, listings update, and markets shift—sometimes faster
                than we can type. So, if anything looks off, please direct your
                concerns to the universe of unpredictable real estate data, not
                us. We’re just the messengers, here to help you navigate {city}
                ’s property landscape with the best insights available.
              </p>
            </Grid>
          </Grid>
        </Container>
      </div>

      <Footer />
    </>
  )
}

export default MarketStatistics
